import React, { useState } from "react";
import {
  X,
  User,
  Mail,
  Lock,
  ShieldCheck,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  LogOut,
  Save,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function ProfileModal({ isOpen, onClose, onSignOut }) {
  const { user, isAdmin, updateProfile, signOut } = useAuth();

  const [fullName, setFullName] = useState(
    user?.user_metadata?.full_name || ""
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  if (!isOpen || !user) return null;

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (newPassword && newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const payload = { fullName };
      if (newPassword) {
        payload.password = newPassword;
      }

      await updateProfile(payload);
      setSuccessMessage("Profile updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message || "Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOutClick = async () => {
    try {
      await signOut();
      onClose();
      if (onSignOut) {
        onSignOut();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : "Recent";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-gray-900 border border-gray-700/80 rounded-2xl shadow-2xl p-6 sm:p-8 overflow-hidden text-white">
        {/* Ambient glow */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-600/20 rounded-full blur-3xl pointer-events-none"></div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-xl hover:bg-gray-800 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header Summary */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-800">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-600 to-blue-500 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-purple-600/30">
            {fullName
              ? fullName.charAt(0).toUpperCase()
              : user.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-white">
                {fullName || "User Profile"}
              </h3>
              {isAdmin ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold rounded-full">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Admin
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs font-semibold rounded-full">
                  Pro Member
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" />
              Member since {memberSince}
            </p>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/40 border border-red-500/50 rounded-xl flex items-start gap-2.5 text-red-200 text-sm">
            <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-900/40 border border-green-500/50 rounded-xl flex items-center gap-2.5 text-green-200 text-sm">
            <CheckCircle className="h-5 w-5 text-green-400 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Edit Form */}
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="John Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="email"
                disabled
                value={user.email}
                className="w-full pl-11 pr-4 py-2.5 bg-gray-800/40 border border-gray-700/60 rounded-xl text-gray-400 text-sm cursor-not-allowed"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-gray-800">
            <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
              Change Password (optional)
            </label>
            <div className="space-y-2">
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                />
              </div>

              {newPassword && (
                <div className="relative animate-in fade-in duration-150">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-11 pr-4 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 mt-6 border-t border-gray-800">
            <button
              type="button"
              onClick={handleSignOutClick}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-950/30 border border-red-800/40 rounded-xl transition-all"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-medium text-sm rounded-xl transition-all shadow-md shadow-purple-600/20 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProfileModal;
